// Shared node-card pieces for a list of settings: one labeled control per
// setting (number / checkbox / select / text, the label carrying the setting's
// `description` as a hover tooltip), grouped under collapsible `section` titles.
// Used by the generic plugin card (PluginNode) and the Look node (LookNode).

import { useState } from 'react'
import { nodeControlClass } from '../../lib/nodeControlClass.js'

export function renderControl(pin, value, onChange) {
  if (pin.type === 'boolean') {
    return (
      <input
        className={nodeControlClass()}
        type="checkbox"
        checked={!!value}
        onChange={e => onChange?.(pin.id, e.target.checked)}
        onMouseDown={e => e.stopPropagation()}
        style={{ cursor: 'pointer' }}
      />
    )
  }
  if (pin.type === 'number') {
    return (
      <input
        className={nodeControlClass()}
        type="number"
        value={value ?? 0}
        step={pin.step ?? 0.01}
        min={pin.min}
        max={pin.max}
        onChange={e => onChange?.(pin.id, parseFloat(e.target.value))}
        onMouseDown={e => e.stopPropagation()}
        style={{
          width: 56, boxSizing: 'border-box',
          background: '#0a1520', border: '1px solid #1f3850',
          color: '#9ad0f0', padding: '2px 4px',
          borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
        }}
      />
    )
  }
  if (pin.type === 'select') {
    return (
      <select
        className={nodeControlClass()}
        value={value ?? pin.default ?? ''}
        onChange={e => onChange?.(pin.id, e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        style={{
          background: '#0a1520', border: '1px solid #1f3850',
          color: '#9ad0f0', padding: '2px 4px',
          borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
          cursor: 'pointer',
        }}
      >
        {(pin.options ?? []).map(o => {
          const ov = typeof o === 'string' ? o : o.value
          const ol = typeof o === 'string' ? o : (o.label ?? o.value)
          return <option key={ov} value={ov}>{ol}</option>
        })}
      </select>
    )
  }
  // string / unknown: text input
  return (
    <input
      className={nodeControlClass()}
      type="text"
      value={value ?? ''}
      onChange={e => onChange?.(pin.id, e.target.value)}
      onMouseDown={e => e.stopPropagation()}
      style={{
        width: 80, boxSizing: 'border-box',
        background: '#0a1520', border: '1px solid #1f3850',
        color: '#9ad0f0', padding: '2px 4px',
        borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
      }}
    />
  )
}

/** One labeled setting row (no handle) — label carries the pin's tooltip. */
export function ConfigRow({ pin, data }) {
  const value = data[pin.id] ?? pin.default
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
      <span
        title={pin.description}
        style={{ color: '#708898', fontSize: 10, cursor: pin.description ? 'help' : undefined }}
      >{pin.label ?? pin.id}</span>
      {renderControl(pin, value, data.onChange)}
    </div>
  )
}

// The collapse toggle row shared by section titles and "Advanced".
export function ToggleRow({ open, onToggle, label, count, title, bold, first }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      onMouseDown={e => e.stopPropagation()}
      title={title}
      style={{
        display: 'flex', alignItems: 'center', gap: 4, width: '100%',
        background: 'none', border: 'none', borderTop: first ? 'none' : '1px solid #1a2a36',
        color: bold ? '#a0b4c4' : '#7a90a0', fontSize: bold ? 9 : 10, fontFamily: 'var(--m-font)',
        fontWeight: bold ? 'bold' : 'normal', letterSpacing: bold ? 0.6 : 0,
        textTransform: bold ? 'uppercase' : 'none',
        padding: first ? '0 0 3px' : '5px 0 3px', marginTop: first ? 0 : 2,
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{ display: 'inline-block', width: 9 }}>{open ? '▾' : '▸'}</span>
      {label}
      <span style={{ color: '#4a5a68', marginLeft: 'auto', fontWeight: 'normal' }}>{count}</span>
    </button>
  )
}

/**
 * Config rows grouped by `section` (manifest order). Each section's title is a
 * collapse toggle; pins with no section render as plain rows.
 */
export function ConfigRows({ pins, data, collapsed, toggle, firstRow = true }) {
  const rows = []
  for (let i = 0; i < pins.length;) {
    const section = pins[i].section
    let j = i
    while (j < pins.length && pins[j].section === section) j++
    const group = pins.slice(i, j)
    if (section) {
      const open = !collapsed.has(section)
      rows.push(
        <ToggleRow key={`section:${section}`} bold first={firstRow && rows.length === 0}
          open={open} onToggle={() => toggle(section)} label={section} count={group.length}
          title={open ? `Collapse ${section}` : `Expand ${section}`} />,
      )
      if (open) for (const pin of group) rows.push(<ConfigRow key={pin.id} pin={pin} data={data} />)
    } else {
      for (const pin of group) rows.push(<ConfigRow key={pin.id} pin={pin} data={data} />)
    }
    i = j
  }
  return rows
}

/** Collapsed-section state for a card: sections start open; view state only, not saved. */
export function useCollapsedSections() {
  const [collapsed, setCollapsed] = useState(() => new Set())
  const toggle = (name) => setCollapsed(prev => {
    const next = new Set(prev)
    if (next.has(name)) next.delete(name)
    else next.add(name)
    return next
  })
  return [collapsed, toggle]
}
